"use client";

import '@aws-amplify/ui-react/styles.css';
import "./globals.css";
import { Authenticator, Card, Button, useTheme, View, Text } from '@aws-amplify/ui-react';
import React, { FormEvent, useState, useEffect, useRef, useCallback } from "react";
import { generateRecipe, generateResultFromWix, generateDataTitan, generateResultFromElfsight,
  generateDataTitanFromString, generateResponse, pushLogToCloudWatch, fetchTopChunks,
  validateClaudeContext, cleanChunk, fetchTopChunksByDocType, testDatabaseContents } from "./actions";
import Image from 'next/image';
import Link from 'next/link';
import debounce from 'lodash.debounce';
import ThemeToggle from '@src/components/ThemeToggle';
import ChatToggle from '@src/components/ChatToggle';
import { KBRow } from "@src/types/kb";

// Add a new interface for chat messages
interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

if (process.env.NODE_ENV === 'production') {
  console.log = function () { };
  console.warn = function () { };
  console.error = function () { };
}

const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        {/* <Image
        src="/images/logo.svg"
            alt="Suptamins Logo"
            width={100}
            height={100}
            className="object-contain w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28"
          /> */}
        <div className="text-left p-4 mt-auto text-blue-600">
          <br></br>
          1. Create Account.
          <br></br>
          2. Type in the name of the vitamin or mineral you are looking for and
          Suptamins will recommend its top pick based off of calculated Suptamins scores
          of different products containing that ingredient.
        </div>
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <div className="text-center p-4 mt-auto">
          <Text color={"#A60800"}>
            <a href="mailto:help@suptamins.com">help@suptamins.com</a>
            <br />
            &copy; 2022 BY SUPTAMINS
          </Text>
        </div>
      </View>
    );
  }
};

function ExpandableList({ title, items }: { title: string; items: string[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="text-center">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="font-medium text-lg text-green-800 dark:text-green-400 underline"
      >
        {title} {isExpanded ? '-' : '+'}
      </button>
      {isExpanded && (
        <ul className="list-none mt-2 ml-6 text-green-800 dark:text-green-400">
          {items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ---- helpers ----
function cleanUrl(u?: string | null) {
  return u ? u.replace(/[)\]">;,]+$/g, "").trim() : "";
}
function isNoLink(s?: string) {
  const v = (s || "").toLowerCase();
  return v === "no link" || v === "no links" || v === "no link avail";
}
function firstAmazonUrl(s?: string) {
  if (!s) return "";
  const m = s.match(/https?:\/\/(?:www\.)?amazon\.[a-z.]+\/\S+/i);
  return m ? cleanUrl(m[0]) : "";
}
function firstAnyUrl(s?: string) {
  if (!s) return "";
  const m = s.match(/https?:\/\/\S+/i);
  return m ? cleanUrl(m[0]) : "";
}

// try to recover a title from a messy chunk:
//  - "title": "..."
 // - or leading text before an em dash:   <Title> — Suptamins...
function extractTitleFromChunk(chunk?: string) {
  if (!chunk) return "";
  const m1 = chunk.match(/"title"\s*:\s*"([^"]+)"/i);
  if (m1?.[1]) return m1[1].trim();
  const m2 = chunk.match(/^\s*([^—\-|]{3,}?)\s+[—\-|]\s+/); // text before dash
  if (m2?.[1]) return m2[1].trim().replace(/^["']|["']$/g, "");
  return "";
}

function normalizeTitle(t?: string | null) {
  if (!t) return "";
  return t
    .toLowerCase()
    .replace(/[®™©]/g, "")
    .replace(/[^a-z0-9+ ]/g, " ")
    .replace(/\s+(capsules?|softgels?|vsoftgel|drops|liquid|kids|sustainable|complex)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
function jaccard(a: string, b: string) {
  const A = new Set(a.split(" ").filter(Boolean));
  const B = new Set(b.split(" ").filter(Boolean));
  if (!A.size || !B.size) return 0;
  let inter = 0;
  A.forEach(x => { if (B.has(x)) inter++; });
  return inter / (A.size + B.size - inter);
}

// pick affiliate or product link from a score row
function linkFromScoreRow(s: any) {
  const aff = cleanUrl(s.amazon_affiliate_link || s.meta?.amazon_affiliate_link);
  if (aff && !isNoLink(aff)) return aff;
  const affFromChunk = firstAmazonUrl(s.chunk);
  if (affFromChunk) return affFromChunk;
  const prod = cleanUrl(s.product_link || s.meta?.product_link);
  if (prod) return prod;
  const anyFromChunk = firstAnyUrl(s.chunk);
  return anyFromChunk || "";
}

// robust title getter for a score row
function scoreTitle(s: any) {
  return (s.title || s.meta?.title || extractTitleFromChunk(s.chunk) || "").trim();
}

// robust title getter for a product row
function productTitle(p: any) {
  return (p.title || p.meta?.title || extractTitleFromChunk(p.chunk) || "").trim();
}

// ---- main: enrich products with affiliate links from scores (no SQL changes) ----
function attachAffiliateLinksToProducts(topP: any[], topS: any[]) {
  // index score rows by normalized title
  const scoreIndex = new Map<string, any[]>();
  for (const s of topS) {
    const t = scoreTitle(s);
    const k = normalizeTitle(t);
    if (!k) continue;
    const arr = scoreIndex.get(k) || [];
    arr.push(s);
    scoreIndex.set(k, arr);
  }

  return topP.map(p => {
    // keep if product already has affiliate link
    const directAff = cleanUrl(p.amazon_affiliate_link || p.meta?.amazon_affiliate_link);
    if (directAff && !isNoLink(directAff)) return p;

    const pT = productTitle(p);
    const pK = normalizeTitle(pT);
    let chosen = "";

    // exact normalized title match
    if (pK && scoreIndex.has(pK)) {
      for (const s of scoreIndex.get(pK)!) {
        chosen = linkFromScoreRow(s);
        if (chosen) break;
      }
    }

    // small fuzzy fallback if nothing yet
    if (!chosen && pK) {
      let best: any = null, bestSim = 0;
      for (const [k, arr] of scoreIndex.entries()) {
        const sim = jaccard(k, pK);
        if (sim > bestSim) {
          const candidate = arr.find(x => linkFromScoreRow(x));
          if (candidate) { best = candidate; bestSim = sim; }
        }
      }
      if (best && bestSim >= 0.45) chosen = linkFromScoreRow(best);
    }

    if (!chosen) return p;
    
    return { ...p, amazon_affiliate_link: chosen }; // non-destructive
  });
}

function isNoLinkSentinel(s?: string) {
  if (!s) return false;
  const v = s.toLowerCase().trim();
  return v === "no link avail" || v === "no links" || v === "no link";
}

function normalizeUrl(u?: string) {
  if (!u) return "";
  try {
    const url = new URL(u);
    ["utm_source","utm_medium","utm_campaign","utm_term","utm_content","tag","linkId","dib","dib_tag","qid","sr","keywords","ref_"]
      .forEach(p => url.searchParams.delete(p));
    return url.origin + url.pathname;
  } catch {
    return u.replace(/\/+$/,"");
  }
}

function urlsEqual(a?: string, b?: string) {
  if (!a || !b) return false;
  return normalizeUrl(a) === normalizeUrl(b);
}

function firstUrlFromText(s?: string) {
  if (!s) return "";
  const m = s.match(/https?:\/\/\S+/);
  return m ? cleanUrl(m[0]) : "";
}

function getAffiliateLink(r: any) {
  // 1) direct on product row
  const direct = cleanUrl(r.amazon_affiliate_link || r.meta?.amazon_affiliate_link);
  if (direct && !isNoLink(direct)) return direct;

  // 2) fall back to product_link if no affiliate link
  const productLink = cleanUrl(r.product_link || r.meta?.product_link);
  if (productLink) return productLink;

  return "";
}

function getCardTitle(r: any, pageTopic?: string) {
  return (r.title || r.meta?.title || extractTitleFromChunk(r.chunk) || pageTopic || "Product").trim();
}

// Your existing “primary” link logic preserved
function pickLink(r: any) {
  const a = cleanUrl(r.amazon_affiliate_link || r.meta?.amazon_affiliate_link);
  if (a && !isNoLinkSentinel(a)) return a;
  const p = cleanUrl(r.product_link || r.meta?.product_link);
  if (p) return p;
  return firstUrlFromText(r.chunk);
}



export default function Home() {
  // HOOKS
  // it returns the router instance if it is rendered in the pages router
  // returns null if it is in the app router
  // const router = useRouter();
  // const searchParams = useSearchParams(); // Hook at the top level

  const [result, setResult] = useState<string>("");
  const [err, setErr] = useState<string>("");
  const [resultWix, setResultWix] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [pause, setPausing] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isAppRouter, setIsAppRouter] = useState(false);
  const [isSmallViewport, setIsSmallViewport] = useState(true);
  const [visitorIP, setVisitorIP] = useState("unknown");
  const visitorIPRef = useRef(visitorIP);
  const [linkSource, setLinkSource] = useState("unknown");
  const linkSourceRef = useRef(linkSource);
  const searchKeywordRef = useRef(searchKeyword);
  const debouncedSubmitRef = useRef<DebouncedFunction | null>(null);
  const [topP, setTopP] = useState<KBRow[]>([]);
  const [amazonLink, setAmazonLink] = useState<string>("");
  const [found, setFound] = useState<boolean>(false);
  
  // New state variables for chat mode
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Update ref when searchKeyword changes
  type DebouncedFunction = ReturnType<typeof debounce>;

  useEffect(() => {
    searchKeywordRef.current = searchKeyword;
  }, [searchKeyword]);

  // MOCK FUNCTION
  const MOCKpushLogToCloudWatch = async (source: string) => {
    // Mocking CloudWatch push (replace this with your actual implementation)
    return Promise.resolve(`Logged to CloudWatch: ${source}`);
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const sourceFromParams = params?.get("source") || "unknown";
      setLinkSource(sourceFromParams);
      // console.log("Link source:", sourceFromParams);
      const jsonString = JSON.stringify({
        session: "start",
        linkSource: sourceFromParams // Use immediate value
      });
      fetchVisitorIP();
      onLogToCloudWatch(jsonString);
    }
  }, []); // Dependency on searchParams ensures correct reactivity

  useEffect(() => {
    linkSourceRef.current = linkSource; // Keep the ref updated
  }, [linkSource]);

  // Update the ref whenever the state changes
  useEffect(() => {
    visitorIPRef.current = visitorIP;
    if (visitorIP !== "") {
      const jsonString = JSON.stringify({
        session: "start",
        visitorIP: visitorIP // Use immediate value
      });
      onLogToCloudWatch(jsonString);
    }
  }, [visitorIP]);

  const fetchVisitorIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json'); // Example API to get IP
      const data = await response.json();
      setVisitorIP(data.ip); // Store IP in state
    } catch (e) {
      setErr("");
      console.error("Error fetching IP address:", e);
      setErr(`${e}`);
    }
  }

  const onLogToCloudWatch = async (source: string) => {

    try {
      // console.log('CloudWatch input:', source);
      if (source.length === 0) {
        source = linkSourceRef.current;
      }
      let currentVisitorIP = visitorIPRef.current;
      if (currentVisitorIP === "") {
        currentVisitorIP = "unknown";
      }
      // Organize the log data into a JSON object
      const logData = {
        visitorIP: currentVisitorIP,
        source: source,
        timestamp: new Date().toISOString(),
      };
      const result = await pushLogToCloudWatch(JSON.stringify(logData));
      console.log('CloudWatch log data:', logData);
      console.log('CloudWatch output:', result);
    } catch (e) {
      setErr("");
      console.error(`An error occurred: ${e}`);
      setErr(`${e}`);
    } finally {
    }
  }

  useEffect(() => {
    // get request information from the client
    if (typeof window === "object") {
      console.log('Client side rendering');
    } else {
      console.log('Server side rendering');
      throw new Error(`NOT Client side rendering ${typeof window}`);
    }
    setIsClient(true); // Marks the component as rendered on the client side
    const checkViewport = () => {
      setIsSmallViewport(window.innerWidth < 950);
    };
    // Throttle the resize listener to prevent excessive updates
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkViewport, 100); // Adjust delay as needed
    };

    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', handleResize);

    // Run check on initial load
    checkViewport();
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    setLoading(true);
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const data = await generateRecipe(formData);
      const recipe = typeof data === "string" ? data : "No data returned";
      setResult(recipe);
      setErr("");
    } catch (e) {
      console.error(`An error occurred: ${e}`);
      setErr(`An error occurred: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitTitan = async (event: FormEvent<HTMLFormElement>) => {
    setLoading(true);
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const data = await generateDataTitan(formData);
      const response = data;
      // const response = typeof data === "string" ? data : "No data returned";
      console.log('Titan response:', response);
      setResult(response);
      setErr("");
    } catch (e) {
      console.error(`An error occurred: ${e}`);
      setErr(`An error occurred: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  const onGetResultFromWix = async () => {
    setLoading(true);
    try {
      // clear previous results
      setResult("");
      setResultWix("");
      // vitamin d, vitamin k, calcium
      const result = await generateResultFromWix(searchKeywordRef.current);
      const response = typeof result === "string" ? result : "No data returned";
      console.log('Wix result:', response);
      setResult(response);
      setResultWix(response);
      setErr("");
    } catch (e) {
      console.error(`An error occurred: ${e}`);
      setErr(`${e}`);
    } finally {
      setLoading(false);
    }
  };

  const identifyErrorAndReactions = async (error: string) => {
    // add 60 seconds delay
    if (!error.includes("Too many requests, please wait before trying again")) {
      return;
    }
    setPausing(true);
    setErr("");
    setErr("Too many requests, please wait 60 seconds before trying again. This wil disappear automatically.");
    await new Promise((resolve) => setTimeout(resolve, 60000));
    setErr("");
    const currentKeyword = searchKeywordRef.current;
    const currentLinkSource = linkSourceRef.current;
    const jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, msg: `finished waiting for 60 seconds` });
    onLogToCloudWatch(jsonString);
    setPausing(false);
  };

  const onGoSuptamins = useCallback(async () => {
    if (loading) {
      return; // Prevent multiple submissions
    }
    setLoading(true);
    const currentKeyword = searchKeywordRef.current;
    const currentLinkSource = linkSourceRef.current; 
    
    // Add user message to chat in chat mode
    if (chatMode && currentKeyword.trim()) {
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        text: currentKeyword,
        isUser: true,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, userMessage]);
    }
    
    try {
      // clear previous results
      setResult("");
      setResultWix("");
      setTopP([]); // Clear topP state
      setErr("");
      if (typeof currentKeyword !== 'string' || currentKeyword.trim().length === 0) {
        console.error('Invalid or empty search keyword.');
        throw new Error('Invalid or empty search keyword. Please enter a valid prompt.');
      }
      if (typeof currentLinkSource !== 'string' || currentLinkSource.trim().length === 0) {
        console.error('Invalid or empty link source.');
        throw new Error('Invalid or empty link source.');
      }
      // vitamin d, vitamin k, calcium
      const jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, button: "Go Suptamins" });
      onLogToCloudWatch(jsonString);
      let result = "";

      // const _string_prompt = "Human expert input provided by Suptamins please use this information to generate a response " + "search:" + currentKeyword;
      // 1. Titan embed
      console.log('currentKeyword:', currentKeyword);
      console.log('currentKeyword length:', currentKeyword.length);
      if (currentKeyword.trim().length === 0) {
        console.error('Invalid or empty search keyword.');
        throw new Error('Invalid or empty search keyword. Please enter a valid prompt.');
      }
      const _string_prompt = currentKeyword.trim();
      console.log('string prompt:', _string_prompt);
      console.log('string prompt length:', _string_prompt.length);
      const qv = await generateDataTitanFromString(result, _string_prompt);
      console.log('query vector:', qv);

      // 2. Retrieve per doc-type and build clean, tagged strings
      const topP = await fetchTopChunksByDocType(qv, 'suptamins_product', 3);
      const topA = await fetchTopChunksByDocType(qv, 'suptamins_article', 2);
      const topS = await fetchTopChunksByDocType(qv, 'suptamins_score', 3);

      // Store topP for display
      // setTopP(topP);

      console.log('topP:', topP);
      console.log('topA:', topA);
      console.log('topS:', topS);

      if (topP.length === 0 && topA.length === 0 && topS.length === 0) {
        console.error('No matching chunks found in vector database');
        throw new Error("No matching chunks found in vector database");
      }

      // title → score map from kb-scores
      const scoreMap = new Map<string, number>();
      const affiliateByTitle = new Map<string, string>();
      topS.forEach(s => {
        const t = s.title || s.meta?.title;
        if (!t) return;
        const aff = cleanUrl(s.amazon_affiliate_link || s.meta?.amazon_affiliate_link);
        const prod = cleanUrl(s.product_link || s.meta?.product_link);
        const chosen = (!isNoLink(aff) && aff) ? aff : prod;
        if (chosen) affiliateByTitle.set(t, chosen);
      });


      // Store enhanced topP for display
      const topPEnriched = attachAffiliateLinksToProducts(topP, topS);
      setTopP(topPEnriched);
      console.log('topPEnriched:', topPEnriched);
      // Store amazon link in state for later use - with safe fallback
      const firstProductLink = topPEnriched[0]?.amazon_affiliate_link || 
                              topPEnriched.find(p => {
                                const link = p.amazon_affiliate_link;
                                return link && (
                                  (link.includes('amazon.ca/') || link.includes('//amazon.ca')) ||
                                  link.includes('tag=suptamins')
                                );
                              })?.amazon_affiliate_link ||
                              "";
      console.log('firstProductLink:', firstProductLink);
      setAmazonLink(firstProductLink);
      console.log('amazonLink set to:', firstProductLink); // This shows what it will become

      // helper to make a printable chunk
      const tagChunks = (rows: any[], tag: 'P'|'A'|'S') =>
        rows.map((r, i) => {
          const title = r.title ?? r.meta?.title ?? 'Unknown';
          const score = (r.suptamins_score ?? scoreMap.get(title)) ?? 'N/A';
          const header =
            tag === 'A'
              ? `[${tag}${i+1}] ${title}\n\n`
              : `[${tag}${i+1}] ${title} — Suptamins score: ${score} / 10\n\n`;
          return header + cleanChunk(r.chunk || '');
        });

      const Pchunks = validateClaudeContext(tagChunks(topP, 'P'), 3);
      const Achunks = topA.length ? validateClaudeContext(tagChunks(topA, 'A'), 2) : [];
      const Schunks = topS.length ? validateClaudeContext(tagChunks(topS, 'S'), 1) : [];

      // Build a small list of primary product links (prefer affiliate)
      const linkEntries = topP
        .map((r, i) => {
          const title = r.title || r.meta?.title || `Product ${i+1}`;
          const link  = pickLink(r);
          return link ? `[L${i+1}] ${title} — ${link}` : '';
        })
        .filter(Boolean)
        .slice(0, 3); // keep it short for the prompt

      const ingredients = [
        ...Pchunks,
        ...Achunks,
        ...Schunks,
        ...(linkEntries.length ? [`\nLinks:\n${linkEntries.join('\n')}`] : []),
        `USER_QUESTION: ${currentKeyword.trim()}`
      ];

      console.log('Claude req:', ingredients);

      // 3. Call Claude with SupAI-AnswerBot system prompt
      let finalResp = await generateResponse(currentKeyword.trim(), ingredients);
      console.log('Claude final resp:', finalResp);


      // 5. Parse Claude response
      let found_in_chunk = false;
      let foundValue = false;
      let record;
      let Title_keyword = "";
      if (finalResp.includes("NOT_FOUND")) {
        console.warn("Claude returned NOT_FOUND");
        record = null;
        setAmazonLink("");
        foundValue = false;
      } else {
        foundValue = true;
        console.log("Claude returned a product");
        console.log("amazonLink:", amazonLink);
      }
      if (found_in_chunk) {
        // make sure the last result also has this
        if (!finalResp.includes(Title_keyword)) {
          console.warn("Claude did not return the found product");
          record = null;
          setAmazonLink("");
          foundValue = false;
        }
      }
      if (!foundValue || firstProductLink === "") {
        console.warn("No product found");
        record = null;
        setAmazonLink("");
        foundValue = false;
        finalResp += "\n\n" + "We are sorry, we could not find the product you are looking for. Please send your request to help@suptamins.com or use the Provide feedback button.";
      }
      
      setFound(foundValue);

      // Create a simple structured result with the Amazon link
      const structuredResult = {
        "Product": [{
          "amznlink": firstProductLink // Use firstProductLink instead of amazonLink
        }]
      };
      
      // Add bot response to chat in chat mode
      if (chatMode) {
        const botMessage: ChatMessage = {
          id: Date.now().toString(),
          text: finalResp,
          isUser: false,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, botMessage]);
        // Clear input after sending in chat mode
        setSearchKeyword("");
      }
      
      setResult(finalResp);
      // Set the structured data for getValueFromJson to use
      setResultWix(JSON.stringify(structuredResult));
      console.log('structuredResult:', structuredResult);
      setErr("");
    } catch (e) {
      console.error(`An error occurred: ${e}`);
      setErr(`${e}`);
      
      // Add error message to chat in chat mode
      if (chatMode) {
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          text: `Error: ${e}`,
          isUser: false,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, errorMessage]);
      }
      
      const jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, error: `${e}` });
      onLogToCloudWatch(jsonString);
      identifyErrorAndReactions(`${e}`);
    } finally {
      setLoading(false);
    }
  }, [loading, chatMode]);

  useEffect(() => {
    debouncedSubmitRef.current = debounce(() => {
      if (!loading) {
        onGoSuptamins();
      }
    }, 300) as DebouncedFunction;

    // Cleanup on unmount
    return () => {
      debouncedSubmitRef.current?.cancel();
    };
  }, [loading, onGoSuptamins]);

  const onGetResultFromElfsight = async () => {
    setLoading(true);
    try {
      // @todo add id
      const result = await generateResultFromElfsight(searchKeywordRef.current);
      const response = typeof result === "object" ? result : "No data returned";
      console.log('Elfsight result:', typeof result);
      setResult(JSON.stringify(response));
      setResultWix(JSON.stringify(response));
      setErr("");
    } catch (e) {
      console.error(`An error occurred: ${e}`);
      setErr(`${e}`);
    } finally {
      setLoading(false);
    }
  };

  const onDebug = async () => {
    // if (router !== null) {
    if (true) {
      setIsAppRouter(false);
    } else {
      setIsAppRouter(true);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!loading) {
      setSearchKeyword(event.target.value || '');
    }
  };

  const handleInputChangeWix = (event: React.ChangeEvent<HTMLInputElement>) => {
    setResultWix(event.target.value);
  };

  const getValueFromJson = (categoryToCheck: string, keyToCheck: string, notFoundString: string) => {
    try {
      const parsedResult = JSON.parse(resultWix);
      const jsonObject = parsedResult[categoryToCheck][0];
      return keyToCheck in jsonObject
        ? (jsonObject[keyToCheck] ? `${jsonObject[keyToCheck]}` : `Key '${notFoundString}`)
        : `Key '${notFoundString}`;
    } catch (error) {
      return `Invalid JSON string, ${keyToCheck} ${notFoundString}`;
    }
  };

  // Small reusable Buy from Amazon button used in both chat and search modes
  const BuyFromAmazonButton = ({ variant = 'default' }: { variant?: 'default' | 'chat' }) => {
    const amznVal = getValueFromJson("Product", "amznlink", "not found");
    const isNotFound = amznVal.includes("not found");
    const disabled = !found || isNotFound;

    const baseClass = variant === 'chat'
      ? (disabled ? "opacity-70 dark:text-white text-blue-950 dark:border-gray-700 border-gray-300" : "bg-[#3D9BE9] dark:text-white text-gray-800 hover:bg-[#2d8ad9] hover:text-white")
      : (disabled ? "opacity-70 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-700" : "bg-[#3D9BE9] text-white hover:bg-[#2d8ad9] hover:text-white border-[#3D9BE9]");

    const handleClick = () => {
      if (variant === 'chat') {
        // keep the extra logging that chat-mode previously used
        console.log('found:', found);
        console.log('getValueFromJson result:', amznVal);
      }
      openAmazonLink();
    };

    return (
      <Button
        type="button"
        name="amznlink"
        onClick={handleClick}
        style={{ minWidth: '120px' }}
        disabled={disabled}
        className={`w-full sm:w-auto ${baseClass}`}
      >
        Buy from Amazon
      </Button>
    );
  };

  const openAmazonLink = () => {
    const link = getValueFromJson("Product", "amznlink", "not found");
    console.log(link);
    let currentKeyword = searchKeywordRef.current;
    if (currentKeyword === "") {
      currentKeyword = "unknown";
    }
    let currentLinkSource = linkSourceRef.current;
    if (currentLinkSource === "") {
      currentLinkSource = "unknown";
    }
    let jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, button: "Buy from Amazon", state: "start" });
    onLogToCloudWatch(jsonString);
    if (!link.includes("not found")) {
      // window.location.href = link;  // Navigate to the URL on the same page
      window.open(link, "_blank");  // Open the URL in a new tab
      jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, button: "Buy from Amazon", state: "done" });
      onLogToCloudWatch(jsonString);
    } else {
      console.error("Amazon link not found");
      setErr("Buy link not found");
    }
  };

  const openFeedbackLink = () => {
    const link = "https://www.suptamins.com/services";
    console.log(link);
    let currentKeyword = searchKeywordRef.current;
    if (currentKeyword === "") {
      currentKeyword = "unknown";
    }
    let currentLinkSource = linkSourceRef.current;
    if (currentLinkSource === "") {
      currentLinkSource = "unknown";
    }
    let jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, button: "Provide Feedback", state: "start" });
    onLogToCloudWatch(jsonString);
    if (!link.includes("not found")) {
      // window.location.href = link;  // Navigate to the URL on the same page
      window.open(link, "_blank");  // Open the URL in a new tab
      let jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, button: "Provide Feedback", state: "Done" });
      onLogToCloudWatch(jsonString);
    } else {
      console.error("Feedback link not found");
      setErr("Buy link 1 not found");
    }
  };

  const openSuptaminsProductLink = () => {
    const link = getValueFromJson("Product", "link-copy-of-product-titleid", "not found");
    const linkFull = "https://suptamins.com" + link;
    console.log(linkFull);
    let currentKeyword = searchKeywordRef.current;
    if (currentKeyword === "") {
      currentKeyword = "unknown";
    }
    let currentLinkSource = linkSourceRef.current;
    if (currentLinkSource === "") {
      currentLinkSource = "unknown";
    }
    let jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, button: "Suptamins Page", state: "start" });
    onLogToCloudWatch(jsonString);
    if (!link.includes("not found")) {
      // window.location.href = link;  // Navigate to the URL on the same page
      window.open(linkFull, "_blank");  // Open the URL in a new tab
      let jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, button: "Suptamins Page", state: "Done" });
      onLogToCloudWatch(jsonString);
    } else {
      console.error("Suptamins link not found");
      setErr("Suptamins link not found");
    }
  };

  const clearPageClick = () => {
    let stop = false;
    let button = "Clear Search";
    if (loading) {
      stop = true;
      button = "Clear Search blocked - Loading";
    }
    if (pause) {
      stop = true;
      button = "Clear Search blocked - Paused";
    }
    let currentKeyword = searchKeywordRef.current;
    if (currentKeyword === "") {
      currentKeyword = "unknown";
    }
    let currentLinkSource = linkSourceRef.current;
    if (currentLinkSource === "") {
      currentLinkSource = "unknown";
    }
    const jsonString = JSON.stringify({ searchKeyword: currentKeyword, linkSource: currentLinkSource, button: button });
    onLogToCloudWatch(jsonString);
    if (stop) {
      return;
    }
    window.location.reload(); // Reloads the current page
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default behavior
      debouncedSubmitRef.current?.();
    }
  };

  const handleButtonClick = () => {
    debouncedSubmitRef.current?.();
  };

  // Update chat mode when localStorage changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedMode = localStorage.getItem('chatMode');
      setChatMode(savedMode === 'true');
      
      const handleChatModeChange = () => {
        const updatedMode = localStorage.getItem('chatMode') === 'true';
        setChatMode(updatedMode);
      };
      
      window.addEventListener('chatModeChange', handleChatModeChange);
      return () => {
        window.removeEventListener('chatModeChange', handleChatModeChange);
      };
    }
  }, []);

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (chatMode && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatMode]);

  // Clear chat history
  const clearChat = () => {
    setChatMessages([]);
    setResult("");
    setResultWix("");
    setSearchKeyword("");
    setErr("");
    setFound(false);
  };

  // Render chat interface or standard interface based on mode
  const renderInterface = () => {
    if (chatMode) {
      return (
        <>
          <div className="flex flex-col w-full border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="flex-1 p-4 overflow-y-auto max-h-[50vh] min-h-[300px] bg-gray-50 dark:bg-gray-900">
              {chatMessages.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  Start a conversation by typing a message below
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`mb-4 ${msg.isUser ? 'text-right' : 'text-left'}`}
                  >
                    <div 
                      className={`inline-block p-3 rounded-lg max-w-[90%] md:max-w-[80%] ${
                        msg.isUser 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                      <div className={`text-xs mt-1 ${msg.isUser ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>
                        {msg.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
            
            <div className="p-4 border-t border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <input
                  type="text"
                  id="ingredients"
                  name="ingredients"
                  required
                  placeholder="Type your message..."
                  value={searchKeyword || ''}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  className={`w-full border text-gray-900 dark:text-white dark:bg-gray-900 p-3 rounded-lg ${
                    loading ? 'bg-gray-200 dark:bg-gray-800 cursor-not-allowed' : 'border-black dark:border-gray-700'
                  }`}
                  aria-disabled={loading}
                />
                <Button
                  backgroundColor="#3D9BE9"
                  color="white"
                  type="button"
                  onClick={handleButtonClick}
                  disabled={loading || pause}
                  className="w-full sm:w-auto py-2 px-4 rounded-lg text-sm dark:bg-blue-700 dark:hover:bg-blue-600 mt-2 sm:mt-0"
                  style={{ minWidth: '80px' }}
                >
                  {loading ? 'Sending...' : 'Send'}
                </Button>
              </div>
              
              <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                <Button
                  backgroundColor="#A60800"
                  color="white"
                  type="button"
                  onClick={clearChat}
                  className="text-white py-2 px-4 rounded-lg text-sm dark:bg-red-900 dark:hover:bg-red-800 w-full sm:w-auto"
                  style={{ minWidth: '120px' }}
                >
                  Clear chat
                </Button>
              </div>
            </div>
          </div>
          
          {/* Separate box for disabled buttons */}
          {result && (
            <div className="mt-4 p-4 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
              <h3 className="text-center text-sm text-gray-500 dark:text-gray-400 mb-3">Purchase Options</h3>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <div className="relative group">
                  <BuyFromAmazonButton variant="chat" />
                  {getValueFromJson("Product", "amznlink", "not found").includes("not found") && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                      Coming Soon
                    </div>
                  )}
                </div>
                
                <div className="relative group">
                  <Button
                    color="white"
                    type="button"
                    name="amznlink"
                    onClick={openAmazonLink}
                    style={{ minWidth: '120px' }}
                    disabled={true}
                    className="w-full sm:w-auto opacity-70 dark:text-white text-gray-900 dark:border-gray-700 border-gray-300"
                  >
                    Buy from Suptamins
                  </Button>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    Coming Soon
                  </div>
                </div>
                
                {/* <div className="relative group">
                  <Button
                    color="white"
                    type="button"
                    name="suptaminslink"
                    onClick={openSuptaminsProductLink}
                    style={{ minWidth: '120px' }}
                    disabled={true}
                    className="w-full sm:w-auto opacity-70"
                  >
                    Suptamins page
                  </Button>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    Coming Soon
                  </div>
                </div> */}
              </div>
            </div>
          )}
        </>
      );
    } else {
      // Original interface
      return (
        <>
          <form
            className="p-4 flex flex-col items-center justify-center gap-4 w-full mx-auto"
          >
            <div className="flex flex-col w-full">
              <div className="relative w-full">
                <input
                  type="text"
                  id="ingredients"
                  name="ingredients"
                  required
                  placeholder="How can we help you today?"
                  value={searchKeyword || ''}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  className={`border text-gray-900 dark:text-white dark:bg-gray-900 p-4 rounded-lg text-xl w-full ${
                    loading ? 'bg-gray-200 dark:bg-gray-800 cursor-not-allowed' : 'border-black dark:border-gray-700'
                  }`}
                  aria-disabled={loading}
                />
              </div>
              
              <div className="flex flex-wrap items-center justify-center gap-4 mt-4">
                <Button
                  backgroundColor="#A60800"
                  color="white"
                  type="button"
                  onClick={clearPageClick}
                  className="text-white py-2 px-4 rounded-lg text-sm dark:bg-red-900 dark:hover:bg-red-800"
                  style={{ height: '40px', minWidth: '150px' }}
                >
                  Clear search
                </Button>
                
                {pause ? (
                  <span
                    className="py-2 px-4 rounded-lg text-sm inline-flex items-center justify-center"
                    style={{
                      backgroundColor: '#3D9BE9',
                      color: 'white',
                      height: '40px',
                      minWidth: '150px',
                      cursor: 'default',
                      userSelect: 'none',
                    }}
                  >
                    Refreshing, wait
                  </span>
                ) : (
                  loading ? (
                    <span
                      className="py-2 px-4 rounded-lg text-sm inline-flex items-center justify-center"
                      style={{
                        backgroundColor: '#3D9BE9',
                        color: 'white',
                        height: '40px',
                        minWidth: '150px',
                        cursor: 'default',
                        userSelect: 'none',
                      }}
                    >
                      Searching
                    </span>
                  ) : (
                    <Button
                      backgroundColor="#3D9BE9"
                      color="white"
                      type="button"
                      onClick={handleButtonClick}
                      className="py-2 px-4 rounded-lg text-sm dark:bg-blue-700 dark:hover:bg-blue-600"
                      style={{ height: '40px', minWidth: '150px' }}
                    >
                      Go Suptamins
                    </Button>
                  )
                )}
              </div>
            </div>
          </form>
          
          {/* Separate box for purchase buttons */}
          {result && (
            <div className="mt-4 p-4 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
              <h3 className="text-center text-sm text-gray-500 dark:text-gray-400 mb-3">Purchase Options</h3>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <div className="relative group">
                  <BuyFromAmazonButton />
                  {getValueFromJson("Product", "amznlink", "not found").includes("not found") && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                      Coming Soon
                    </div>
                  )}
                </div>
                
                {/* <div className="relative group">
                  <Button
                    backgroundColor="#F9B943"
                    color="white"
                    type="button"
                    name="suptaminslink"
                    onClick={openSuptaminsProductLink}
                    className="py-2 px-4 rounded-lg text-sm dark:bg-yellow-700 dark:hover:bg-yellow-600 opacity-70"
                    style={{ height: '40px', minWidth: '150px' }}
                    disabled={true}
                  >
                    Suptamins page
                  </Button>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    Coming Soon
                  </div>
                </div> */}
                
                <div className="relative group">
                  <Button
                    color="white"
                    type="button"
                    name="amznlink"
                    onClick={openAmazonLink}
                    className="py-2 px-4 rounded-lg text-sm dark:bg-green-700 dark:hover:bg-green-600 opacity-70"
                    style={{ height: '40px', minWidth: '150px' }}
                    disabled={true}
                  >
                    Buy from US
                  </Button>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    Coming Soon
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }
  };

  if (!isClient) {
    return <div>Loading...Welcome to Suptamins</div>;
  }

  const testDatabase = async () => {
    console.log('Testing database contents...');
    const result = await testDatabaseContents();
    console.log('Database test result:', result);
    
    try {
      const parsed = JSON.parse(result);
      console.log('Parsed result:', parsed);
      console.log('Diagnostics:', parsed.diagnostics);
    } catch (e) {
      console.log('Could not parse result as JSON');
    }
  };

  return (
    <>
      {/* Logo placed outside of the Authenticator */}
      {/* <div className="fixed top-0 -left-8 p-9 z-50"> */}
      <div className={`${isSmallViewport ? 'top-0' : 'fixed top-4'} left-0.5 p-4 z-50`}>
        {/* <Link href="/" passHref> */}
        <Image
          src="/images/logo.svg"
          alt="Suptamins Logo"
          width={100}
          height={100}
          className={`${isSmallViewport ? 'w-30 h-30' : 'w-16 h-16'} object-contain`}
          style={{ width: isSmallViewport ? '128px' : '64px', height: isSmallViewport ? '128px' : '64px' }} // Ensures aspect ratio is preserved
        // className="object-contain w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28"
        />
        {/* </Link> */}
      </div>

      {/* <Authenticator initialState="signIn" components={components}> */}
      {/* {({ signOut, user }) => ( */}
      <main className="flex min-h-screen flex-col items-center p-4 sm:p-8 md:p-16 lg:p-24 m-auto dark:bg-dark-bg dark:text-dark-text">
        <div className="absolute top-4 right-4 flex items-center gap-4">
          <ChatToggle />
          <ThemeToggle />
        </div>
        <div className="pb-6 md:pb-10 mx-auto text-center flex flex-col items-center max-w-3xl">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            Welcome to SupAI 2.0
          </h1>
          <h2 className="mt-6 md:mt-10 font-medium text-lg md:text-xl max-w-prose text-gray-900 dark:text-white">
            SupAI is a chatbot that can answer questions about supplements, powered by Suptamins.
            <br />
            <br />
          </h2>
        </div>
        <section className="w-full max-w-2xl mx-auto">
          {renderInterface()}
        </section>
        
        {/* Only show the result box when NOT in chat mode */}
        {!chatMode && (
          <>
            {loading ? (
              <div className="flex flex-col items-center gap-4 w-full max-w-2xl mx-auto">
                <h2 className="m-10 font-medium text-xl max-w-prose text-gray-900 dark:text-white">
                  Finding the best supplements for you... This might take up to 30 seconds
                </h2>
              </div>
            ) : (
              <div className="w-full max-w-2xl mx-auto">
                {result ? (
                  <>
                    <section className="mt-10 mx-auto border border-black dark:border-gray-700 rounded-xl overflow-hidden">
                      <Card 
                        className="p-4 flex flex-col items-center gap-4 max-w-full mx-auto text-xl font-semibold dark:text-white"
                        style={{
                          backgroundColor: 'var(--amplify-colors-background-primary)',
                        } as React.CSSProperties}
                      >
                        <h2 className="whitespace-pre-wrap">{result}</h2>
                      </Card>
                    </section>
                    {/* Product Cards Section */}
                  </>
                ) : null}
              </div>
            )}
          </>
        )}
        
        {/* Product Cards Section - Now shows in both chat and search modes */}
        {found && topP && topP.length > 0 && (
          <section className="mt-6 mx-auto w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Related Products — Amazon affiliate links when available
            </h3>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {topP.map((r, i) => {
                const affiliate = getAffiliateLink(r);
                const primary = pickLink(r);
                const title = getCardTitle(r, searchKeywordRef.current);
                const isCanadianAffiliate = affiliate && (
                  (affiliate.includes('amazon.ca/') || affiliate.includes('//amazon.ca')) ||
                  affiliate.includes('tag=suptamins')
                );

                return (
                  <div
                    key={i}
                    className={`p-4 border border-gray-300 dark:border-gray-700 rounded-lg ${
                      isCanadianAffiliate 
                        ? 'bg-orange-100 dark:bg-orange-900' 
                        : 'bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="font-medium text-gray-900 dark:text-white mb-2">
                      {title}
                      {isCanadianAffiliate && (
                        <span className="ml-2 text-orange-600 dark:text-orange-400 text-sm">
                          Amazon CA Affiliated Link
                        </span>
                      )}
                    </div>

                    {/* Affiliate link FIRST */}
                    {affiliate && (
                      <a
                        href={affiliate}
                        target="_blank"
                        rel="sponsored nofollow noopener noreferrer"
                        className="text-blue-600 hover:underline block mb-2"
                        aria-label="Amazon affiliate link to buy"
                        title="Amazon affiliate link"
                      >
                        Amazon affiliate link
                      </a>
                    )}

                    {/* Keep your existing link, but don't duplicate */}
                    {primary && !urlsEqual(primary, affiliate) && (
                      <a
                        href={primary}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 underline block mb-2"
                      >
                        Buy / View
                      </a>
                    )}

                    {r.suptamins_score != null && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Suptamins score: {r.suptamins_score}/10
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="mt-10 w-full max-w-2xl mx-auto">
          {err ? (
            <section className="mt-10 mx-auto border border-black dark:border-gray-700 rounded-xl overflow-hidden">
              <Card 
                className="p-4 flex flex-col items-center gap-4 max-w-full mx-auto text-xl font-semibold"
                style={{
                        backgroundColor: 'var(--amplify-colors-background-primary)',
                } as React.CSSProperties}
              >
                <h2 className="whitespace-pre-wrap text-red-900 dark:text-red-300">Last Error Info:</h2>
                <div className="dark:text-white">{err}</div>
              </Card>
            </section>
          ) : null}
        </div>
        <div className="mt-10 font-medium text-lg max-w-pros text-gray-900 dark:text-white">
          <div className="text-center p-4 mt-auto">
            Click <strong>"Send" or press enter</strong>. This can take about 30 seconds.
          </div>
        </div>
        <div className="mt-10 font-medium text-lg max-w-prose text-gray-900 dark:text-white">
          You can click on the "Buy from Amazon" button to be taken to Amazon direct link to
          purchase the item or click on "Suptamins page" for more information about the product.
        </div>
        <div className="text-center p-4 mt-auto">
          <Button 
            backgroundColor="#3D9BE9" 
            color="white"
            type="button"
            onClick={openFeedbackLink}
            className="mt-4 w-1/5 text-lg ml-1 dark:bg-blue-700 dark:hover:bg-blue-600"
            style={{ height: '35px', minWidth: '190px' }}
          >
            Provide feedback
          </Button>
        </div>
        <div className="text-center p-4 mt-auto">
          <Text color={"#A60800"}>
            <a href="mailto:help@suptamins.com">help@suptamins.com</a>
            <br />
            &copy; 2022 BY SUPTAMINS
          </Text>
        </div>
        {/* <div className="mt-10 w-full max-w-2xl mx-auto">
          <Button
            backgroundColor="#FF6B35"
            color="white"
            type="button"
            onClick={testDatabase}
            className="w-full sm:w-auto px-6 py-3 text-lg font-medium"
          >
            Test Database
          </Button>
        </div> */}
      </main>
      {/* )}
      </Authenticator> */}
    </>
  );
}
